import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "../../lib/utils";

const OFFSET_PX = 8;
const VIEWPORT_MARGIN_PX = 8;
// The pointer has to rest on the trigger before the card opens, so sweeping
// across a paragraph of citations does not flash a card per chip.
const OPEN_DELAY_MS = 300;
// Long enough to cross the gap between the trigger and the card.
const CLOSE_DELAY_MS = 150;

/**
 * A card of content that hangs under its trigger while the pointer rests on it
 * or the keyboard focus is on it, and that the reader can move into (Figma
 * "HoverCard"). Unlike `Tooltip`, which explains its trigger in two lines and
 * lets the pointer through, the card is a surface of its own: it can hold a
 * link or a button, and it stays open while the pointer is over it.
 *
 * Desktop only by nature: a touch opens nothing, the trigger's own tap does
 * what it does. Portalled and `fixed`, placed from the trigger's rect, pulled
 * back inside the viewport and opened above the trigger when the space below
 * would cut it, like `Tooltip`.
 *
 * The trigger owns its focus and its click (the card adds to it, it never
 * replaces it). Escape, a scroll of the page and a resize close the card.
 */
export function HoverCard({
  label,
  trigger,
  className,
  children,
}: {
  /** The card's accessible name. */
  label: string;
  trigger: React.ReactNode;
  /** The card's width (and any other box rule of the caller). */
  className: string;
  children: React.ReactNode;
}): React.JSX.Element {
  const cardId = useId();
  const triggerRef = useRef<HTMLSpanElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState<{
    top: number;
    left: number;
  } | null>(null);

  function schedule(next: boolean, delay: number): void {
    if (timerRef.current !== null) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      setIsOpen(next);
      if (!next) setPosition(null);
    }, delay);
  }

  function cancel(): void {
    if (timerRef.current === null) return;
    clearTimeout(timerRef.current);
    timerRef.current = null;
  }

  function close(): void {
    cancel();
    setIsOpen(false);
    setPosition(null);
  }

  useLayoutEffect(() => {
    if (!isOpen) return;
    const trigger = triggerRef.current;
    const card = cardRef.current;
    if (trigger === null || card === null)
      throw new Error("should not happen: an open hover card without a node");
    const rect = trigger.getBoundingClientRect();
    const { width, height } = card.getBoundingClientRect();
    const maxLeft =
      document.documentElement.clientWidth - width - VIEWPORT_MARGIN_PX;
    const below = rect.bottom + OFFSET_PX;
    const fitsBelow =
      below + height + VIEWPORT_MARGIN_PX <=
      document.documentElement.clientHeight;
    setPosition({
      top: fitsBelow
        ? below
        : Math.max(VIEWPORT_MARGIN_PX, rect.top - OFFSET_PX - height),
      left: Math.max(VIEWPORT_MARGIN_PX, Math.min(rect.left, maxLeft)),
    });
  }, [isOpen]);

  // The card is placed once, from the trigger's rect: a scroll or a resize
  // would leave it beside other text, so either closes it. A scroll inside the
  // card (its own content) does not. Escape closes it wherever the focus is (a
  // card the pointer opened has none of it), and only while it is open: the
  // key is then handled, so the composer's Escape-to-stop leaves it alone.
  useEffect(() => {
    if (!isOpen) return;
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      close();
    };
    const closeOnScroll = (event: Event) => {
      const card = cardRef.current;
      if (card !== null && event.composedPath().includes(card)) return;
      close();
    };
    document.addEventListener("keydown", closeOnEscape);
    window.addEventListener("scroll", closeOnScroll, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("keydown", closeOnEscape);
      window.removeEventListener("scroll", closeOnScroll, true);
      window.removeEventListener("resize", close);
    };
  }, [isOpen]);

  // A pending timer must not fire on an unmounted card.
  useLayoutEffect(() => cancel, []);

  return (
    <>
      <span
        ref={triggerRef}
        className="inline-flex"
        aria-describedby={isOpen ? cardId : undefined}
        onPointerEnter={(event) => {
          if (event.pointerType === "mouse") schedule(true, OPEN_DELAY_MS);
        }}
        onPointerLeave={(event) => {
          if (event.pointerType === "mouse") schedule(false, CLOSE_DELAY_MS);
        }}
        onFocus={() => {
          // Keyboard focus only: a click focuses the trigger too, and the
          // click already does what the trigger does.
          const focused = document.activeElement;
          if (focused !== null && focused.matches(":focus-visible")) {
            cancel();
            setIsOpen(true);
          }
        }}
        onBlur={() => {
          schedule(false, CLOSE_DELAY_MS);
        }}
        onPointerDown={close}
      >
        {trigger}
      </span>
      {isOpen &&
        createPortal(
          <div
            ref={cardRef}
            id={cardId}
            role="dialog"
            aria-label={label}
            // Measured before it is shown: the first pass renders it hidden at
            // the origin, the layout effect places it before paint.
            className={cn(
              "fixed z-9999 overflow-hidden rounded-xl border border-ds-slate-100 bg-ds-white shadow-desktop-dropdown",
              className
            )}
            style={
              position === null
                ? { top: 0, left: 0, visibility: "hidden" }
                : { top: position.top, left: position.left }
            }
            onPointerEnter={cancel}
            onPointerLeave={() => {
              schedule(false, CLOSE_DELAY_MS);
            }}
            onFocus={cancel}
            onBlur={() => {
              schedule(false, CLOSE_DELAY_MS);
            }}
          >
            {children}
          </div>,
          document.body
        )}
    </>
  );
}
